import type {MRT_ColumnDef} from 'mantine-react-table';
import {useMemo} from 'react';
import type {FormSimpleResponse, FormStateEnum} from 'src/api/ProactApiClient';
import type {PillProps} from 'src/design/atoms/Pill';
import {Pill} from 'src/design/atoms/Pill';
import {FormStateDataOptions} from 'src/shared/constants/columnFilterData';
import {getFormPillConfig} from 'src/app/forms/formHelpers';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';

/** A submissions row, optionally an unsynced offline create pending sync. */
export type FormSubmissionRow = FormSimpleResponse & {pendingSync?: boolean};

interface UseFormSubmissionsColumnsProps {
  showFormDefinitionName?: boolean;
}

export const useFormSubmissionsColumns = ({
  showFormDefinitionName = false,
}: UseFormSubmissionsColumnsProps = {}) => {
  const columns = useMemo<MRT_ColumnDef<FormSubmissionRow>[]>(() => {
    const baseColumns: MRT_ColumnDef<FormSubmissionRow>[] = [
      {
        accessorKey: 'internalIdentifier',
        header: 'Identifier',
      },
      {
        accessorKey: 'shortDescription',
        header: 'Description',
      },
      {
        accessorKey: 'creator.name',
        header: 'Created By',
      },
      {
        id: 'updatedAt',
        accessorFn: row => (row.updatedAt ? new Date(row.updatedAt) : null),
        header: 'Update Date',
        filterVariant: 'date',
        Cell: ({cell}) => <>{cell.getValue<Date>().toString().toShortDate()}</>,
      },
      {
        id: 'createdAt',
        accessorFn: row => (row.createdAt ? new Date(row.createdAt) : null),
        header: 'Created Date',
        filterVariant: 'date',
        Cell: ({cell}) => <>{cell.getValue<Date>().toString().toShortDate()}</>,
      },
      {
        id: 'publishedAt',
        accessorFn: row =>
          row.publishedAt ? parseUtcDate(row.publishedAt) : null,
        header: 'Published',
        filterVariant: 'date',
        Cell: ({cell}) => {
          const value = cell.getValue<Date | null>();
          return <>{value ? value.toString().toShortDate() : ''}</>;
        },
      },
      {
        accessorKey: 'state',
        header: 'Status',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: FormStateDataOptions,
        },
        Cell: ({cell, row}) => {
          if (row.original.pendingSync) {
            return <Pill text="Pending sync" color="blue" />;
          }
          const cellValue = cell.getValue<FormStateEnum>();
          const pillConfigs = getFormPillConfig(cellValue);
          return (
            <>
              {pillConfigs.map((pillOptions: PillProps, index: number) => (
                <Pill
                  key={index}
                  text={pillOptions.text}
                  color={pillOptions.color}
                />
              ))}
            </>
          );
        },
      },
    ];

    if (showFormDefinitionName) {
      return [
        {
          accessorKey: 'formDefinition.name',
          header: 'Form Name',
        },
        {
          accessorKey: 'formDefinition.project.name',
          header: 'Project',
        },
        {
          accessorKey: 'formDefinition.category.name',
          header: 'Category',
        },
        ...baseColumns,
      ];
    }

    return baseColumns;
  }, [showFormDefinitionName]);

  return columns;
};
